import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { SlidersHorizontal, X, Check } from "lucide-react";
import { CHART_REGISTRY, SECTION_LABELS } from "./constants";

const SECTIONS_ORDER = ["operationalHealth", "siteLocations", "firmwareMaintenance", "fleetConfig", "deploymentConfig"];

export function ChartPicker({ visibleCharts, onChange, excludeSections = [] }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    const handler = e => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const visibleSet = useMemo(() => new Set(visibleCharts), [visibleCharts]);
  const excludeSet = useMemo(() => new Set(excludeSections), [excludeSections]);
  const filteredRegistry = useMemo(() => CHART_REGISTRY.filter(c => !excludeSet.has(c.section)), [excludeSet]);
  const filteredSections = useMemo(() => SECTIONS_ORDER.filter(s => !excludeSet.has(s)), [excludeSet]);
  const total = filteredRegistry.length;
  const count = visibleCharts.filter(k => filteredRegistry.some(c => c.key === k)).length;

  // Group charts by section for the dropdown
  const grouped = useMemo(() => {
    const map = {};
    filteredRegistry.forEach(c => {
      if (!map[c.section]) map[c.section] = [];
      map[c.section].push(c);
    });
    return map;
  }, [filteredRegistry]);

  const toggle = useCallback(
    key => {
      const next = new Set(visibleCharts);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      onChange([...next]);
    },
    [visibleCharts, onChange]
  );

  const selectAll = useCallback(() => {
    onChange(filteredRegistry.map(c => c.key));
  }, [onChange, filteredRegistry]);

  const clearAll = useCallback(() => {
    onChange([]);
  }, [onChange]);

  const removeChip = useCallback(
    key => {
      onChange(visibleCharts.filter(k => k !== key));
    },
    [visibleCharts, onChange]
  );

  return (
    <div className="chart-picker" ref={wrapRef}>
      {/* Trigger button */}
      <button
        type="button"
        className={`chart-picker__trigger ${open ? "chart-picker__trigger--active" : ""}`}
        onClick={() => setOpen(p => !p)}
      >
        <SlidersHorizontal size={14} />
        Choose Charts
        <span className="chart-picker__count">
          ({count}/{total})
        </span>
      </button>

      {/* Dropdown */}
      {open && (
        <div className="chart-picker__dropdown">
          {filteredSections.map(sectionKey => (
            <div key={sectionKey} className="chart-picker__section">
              <div className="chart-picker__section-title">{SECTION_LABELS[sectionKey]}</div>
              {(grouped[sectionKey] || []).map(chart => {
                const checked = visibleSet.has(chart.key);
                return (
                  <button
                    key={chart.key}
                    type="button"
                    className="chart-picker__item"
                    onClick={() => toggle(chart.key)}
                  >
                    <span className={`chart-picker__checkbox ${checked ? "chart-picker__checkbox--checked" : ""}`}>
                      {checked && <Check size={10} strokeWidth={3} />}
                    </span>
                    <span className="chart-picker__label">{chart.label}</span>
                  </button>
                );
              })}
            </div>
          ))}
          <div className="chart-picker__footer">
            <button type="button" className="chart-picker__link" onClick={selectAll}>
              Select All
            </button>
            <span className="chart-picker__footer-count">
              {count} of {total} selected
            </span>
            <button type="button" className="chart-picker__link" onClick={clearAll}>
              Clear All
            </button>
          </div>
        </div>
      )}

      {/* Active chips */}
      {count > 0 && count < total && (
        <div className="chart-picker__chips">
          {filteredRegistry
            .filter(c => visibleSet.has(c.key))
            .map(c => (
              <span key={c.key} className="chart-picker__chip">
                {c.label}
                <button type="button" className="chart-picker__chip-x" onClick={() => removeChip(c.key)}>
                  <X size={10} />
                </button>
              </span>
            ))}
        </div>
      )}
    </div>
  );
}

ChartPicker.propTypes = {
  visibleCharts: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  excludeSections: PropTypes.arrayOf(PropTypes.string),
};

export default ChartPicker;
